import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DollarSign, Loader2, X } from "lucide-react";
import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { usePipelinesStore } from "@/store/pipelines/pipelines.store";
import DealAmountField from "./formFields/deal.amount.field";
import DealLeadEmailField from "./formFields/deal.lead.email.field";
import DealLeadNameField from "./formFields/deal.lead.name.field";
import DealLeadPhoneField from "./formFields/deal.lead.phone.field";
import DealNameField from "./formFields/deal.name.field";
import DealPipelineField from "./formFields/deal.pipeline.field";
import DealStatusField from "./formFields/deal.status.field";
import DealTagsField from "./formFields/deal.tags.field";
import LeadSelectorField from "./formFields/lead.selector.field";
import { TCreateDealFormData } from "./add.deal.sidebar";

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  leadId?: number;
};

const AddDealSidebarForm: React.FC<Props> = ({ open, onClose, onSubmit, leadId }) => {
  const currentPipelineId = usePipelinesStore((s) => s.currentPipelineId);
  const pipelines = usePipelinesStore((s) => s.entities);
  const currentPipeline = pipelines[currentPipelineId];
  const { setValue, formState, watch } = useFormContext<TCreateDealFormData>();
  const { isSubmitting } = formState;
  const { chooseExistingLeads, pipeline: selectedPipelineId } = watch();

  useEffect(() => {
    usePipelinesStore.getState().getAllPipelines();
  }, []);

  useEffect(() => {
    if (currentPipeline) {
      setValue("pipeline", currentPipeline.id);
      // Set the first status of the current pipeline as default
      if (currentPipeline.status && currentPipeline.status.length > 0) {
        setValue("status", currentPipeline.status[0].id);
      }
    }
  }, [currentPipeline, setValue]);

  // Update status when pipeline changes in the form
  useEffect(() => {
    if (selectedPipelineId && pipelines[selectedPipelineId]) {
      const selectedPipeline = pipelines[selectedPipelineId];
      if (selectedPipeline.status && selectedPipeline.status.length > 0) {
        setValue("status", selectedPipeline.status[0].id);
      }
    }
  }, [selectedPipelineId, pipelines, setValue]);


  const renderLeadMode = () => {
    // Don't show toggle when leadId is provided (opened from lead section)
    if (leadId) return null;

    return (
      <span
        onClick={() => {
          setValue("chooseExistingLeads", !chooseExistingLeads);
          setValue("leadId", undefined);
          setValue("firstName", "");
          setValue("email", "");
          setValue("phone", "");
        }}
        className="cursor-pointer text-[11.5px] font-medium text-brand-300 transition hover:text-brand-200"
      >
        {chooseExistingLeads ? "Create Lead Manually" : "Search Leads"}
      </span>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="flex h-full w-full flex-col gap-0 overflow-hidden border-l border-white/[0.08] bg-gradient-to-b from-ink-800/95 to-ink-900/95 p-0 outline-none backdrop-blur-xl sm:max-w-[400px]">
        {/* Ambient glow (demo drawer) */}
        <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-accent-green/15 blur-3xl" />

        <SheetHeader className="relative shrink-0 border-b border-white/[0.06] px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-accent-green/30 to-emerald-700/10 ring-1 ring-accent-green/30">
                <DollarSign className="h-4 w-4 text-accent-green" />
              </div>
              <div className="text-left">
                <SheetTitle className="text-[16px] font-semibold tracking-tight text-white">Create Deal</SheetTitle>
                <p className="mt-0.5 text-[11.5px] text-slate-400">Add a deal into any pipeline stage</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close create deal"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-white/[0.06] bg-white/[0.02] text-slate-400 transition hover:border-white/[0.14] hover:bg-white/[0.05] hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </SheetHeader>

        <form onSubmit={onSubmit} className="relative flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 custom-scrollbar">
            <DealNameField />

            {!leadId && (
              <>
                {chooseExistingLeads ? (
                  <div className="space-y-1.5">
                    <LeadSelectorField />
                    {renderLeadMode()}
                  </div>
                ) : (
                  <div className="space-y-3 rounded-xl border border-white/[0.06] bg-gradient-to-br from-white/[0.025] to-white/[0.005] p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10.5px] font-medium uppercase tracking-[0.12em] text-slate-500">New lead</span>
                      {renderLeadMode()}
                    </div>
                    <DealLeadNameField />
                    <DealLeadEmailField />
                    <DealLeadPhoneField />
                  </div>
                )}
              </>
            )}

            <DealPipelineField />

            <DealStatusField />

            <DealAmountField />

            <DealTagsField />
          </div>

          {/* Sticky footer */}
          <div className="flex shrink-0 items-center justify-between gap-2 border-t border-white/[0.06] bg-white/[0.015] px-5 py-3.5">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 items-center rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 text-[12.5px] font-medium text-slate-300 transition hover:border-white/[0.14] hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-gradient-to-br from-accent-green to-emerald-700 px-5 text-[12.5px] font-semibold text-white shadow-[0_10px_28px_-8px_rgba(34,197,94,0.55)] ring-1 ring-accent-green/40 transition hover:translate-y-[-1px] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
            >
              {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {isSubmitting ? "Adding Deal..." : "Add Deal"}
            </button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default AddDealSidebarForm;
